<template>
  <div
    class="share-video-wrapper"
    v-if="(videoExists && reachable) || !videoExists"
  >
    <p class="title">{{$tf('Video')}}</p>
    <div>
      <div v-if="!videoExists && !generatingVideo">
        <p>{{$tf('VideoGeneration')}}</p>
        <div v-if="disabled" class="error-msg">
          <p>{{$tf('This function is only available for DICOM files containers')}}</p>
        </div>
        <div class="button-wrapper">
          <v-btn :disabled="disabled" depressed @click="generateVideo" dark class="primary">
            <v-icon class="mx-2" color="white" right>ondemand_video</v-icon>
            {{$tf('Generate video')}}
            <v-spacer style="width: 6px;max-width: 6px"></v-spacer>
          </v-btn>
        </div>
      </div>
      <div v-else-if="!videoExists && generatingVideo">
        <p>{{$tf('VideoGenerating1')}}</p>
        <p>{{$tf('VideoGenerating2')}}</p>
        <v-progress-linear style="visibility: visible" v-bind:indeterminate="true" :active="true"></v-progress-linear>
      </div>
      <div v-else>
        <v-layout class="mb-1 mx-2" row wrap justify-space-around>
          <v-flex xs12>
            <p>{{$tf('VideoExplanation')}}</p>
          </v-flex>
          <v-flex xs12 :class="$vuetify.breakpoint.smAndUp? 'text-xs-center':''">
            <v-btn
              depressed
              disabled="true"
              v-if="shareNativeCompatible()"
              style="min-width: 230px"
              :block="$vuetify.breakpoint.xsOnly"
              :style="$vuetify.breakpoint.xsOnly?'':'margin-left: auto !important;margin-right: auto !important; width: 50%; display: block;'"
              class="ma-0 accent"
              @click="$machine.os !== 'ios'?shareVideoNative:shareVideoNativeIOS"
            >
              <v-icon>{{'share'}}</v-icon>
              <v-spacer style="width: 6px; max-width: 6px"></v-spacer>
              <span>{{$tf('Send video')}}</span>
            </v-btn>
            <v-btn
              v-else
              depressed
              style="min-width: 230px"
              target="_blank"
              :block="$vuetify.breakpoint.xsOnly"
              :style="$vuetify.breakpoint.xsOnly?'':'margin-left: auto !important;margin-right: auto !important; width: 50%; display: block;'"
              dark
              class="ma-0 accent"
              @click="videoMailTo"
            >
              <v-icon>{{'email'}}</v-icon>
              <v-spacer style="width: 6px; max-width: 6px"></v-spacer>
              <span>{{$tf('Send video')}}</span>
            </v-btn>
          </v-flex>
          <v-flex xs12 :class="$vuetify.breakpoint.smAndUp? 'text-xs-center':''">
            <v-btn
              id="share-video-btn"
              depressed
              @click="sendWhatsappNotification(videoId)"
              :block="$vuetify.breakpoint.xsOnly"
              :style="$vuetify.breakpoint.xsOnly?'':'margin-left: auto !important;margin-right: auto !important; width: 50%; display: block;'"
              class="video-btn"
              :disabled="videoId == 0"
              :loading="videoId == 0"
            >
              <img
                width="35px"
                height="80%"
                class="d-inline mr-2"
                src="../../../assets/WhatsApp_Logo_2.png"
                alt="WhatsApp Logo"
              />
              {{$tf('Send to my WhatsApp')}}
            </v-btn>
          </v-flex>
          <v-flex
            v-if="$machine.os !== 'ios'"
            xs12
            :class="$vuetify.breakpoint.smAndUp ? 'text-xs-center mb-3':'my-2'"
          >
            <v-btn
              id="download-video-btn"
              depressed
              style="min-width: 230px"
              :block="$vuetify.breakpoint.xsOnly"
              :style="$vuetify.breakpoint.xsOnly?'':'margin-left: auto !important;margin-right: auto !important; width: 50%; display: block;'"
              :disabled="videoId == 0"
              :loading="videoId == 0"
              dark
              class="primary ma-0 video-btn"
              @click="downloadVideo"
            >
              <v-icon>arrow_downward</v-icon>
              <v-spacer style="width: 6px; max-width: 6px"></v-spacer>
              <span>{{$tf('Download it')}}</span>
            </v-btn>
          </v-flex>
          <v-flex
            xs12
            v-if="videoExists"
            :class="$vuetify.breakpoint.smAndUp ? 'text-xs-center pt-3':'pb-5 pt-3'"
          >
            <p>
              {{$tf('When you share a video we automatically create a Magic Link to it')}}
              {{$tf('You can modify those sharing options at it\'s own sharing panel:')}}
            </p>

            <v-btn
              depressed
              :disabled="disabled"
              :class="$vuetify.breakpoint.smAndUp ? 'text-xs-center':'pb-5'"
              :block="$vuetify.breakpoint.xsOnly"
              flat
              color="accent"
              @click="showVideoSharePanel"
            >{{$tf('Modify video sharing')}}</v-btn>
          </v-flex>
        </v-layout>
      </div>
    </div>
  </div>
</template>

<script>
import {
  copyToClipboard,
  shareNative,
  shareNativeCompatible,
  mailTo
} from '~/helpers/browser'
import Api from '~/store/idonia-api/api.js'
import MainBus from '~/bus'
import { useEventHelper } from '~/helpers/eventHelper'
import { useBackEvents } from '~/commons/events'
import { onMounted, onUnmounted } from '@vue/composition-api'
import { useBusUtils } from '~/commons/busUtils'
import { useWhatsApp } from '~/commons/whatsapp'
const { BusHandlerManager, setHandlerAsPromise } = useBusUtils()

export default {
  name: 'ShareVideoCard',
  props: {
    file: {
      type: Object,
      default: null
    },
    exists: {
      type: Boolean,
      default: false
    },
    videoId: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    videoExists () {
      return this.videoId !== 0 || this.exists
    }
  },
  watch: {
    videoExists () {
      this.$forceUpdate()
    }
  },
  data () {
    return {
      videoURL: '',
      video: null,
      reachable: true,
      generatingVideo: false,
      bhm: new BusHandlerManager(),
      videoPromise: setHandlerAsPromise(MainBus, 'videoReadyToModify', file => {
        this.video = file
      })
    }
  },
  setup (props, { root: { $store, $tf } }) {
    const { sendWhatsappNotification } = useWhatsApp($store, $tf)
    const { subscribe } = useBackEvents()
    const {
      events,
      getEventContext,
      newHandler,
      getEventFile
    } = useEventHelper()
    let unsubscribe = () => {}
    onMounted(() => {
      getEventContext().then(function (context) {
        const videoHandler = newHandler(function (payload) {
          console.log('DEBUG video_completed: ', context, payload)
          MainBus.$emit('videoLoaded', payload.deprecated_file_id)
          getEventFile(payload.deprecated_file_id)
            .then(response => {
              MainBus.$emit('videoReadyToModify', response)
            })
            .catch(e => {
              console.error(e)
            })
        }, context)
        unsubscribe = subscribe(
          events.videoCompleted,
          context.channels,
          videoHandler
        )
      })
    })
    onUnmounted(() => {
      unsubscribe()
    })
    return {
      sendWhatsappNotification
    }
  },
  created () {
    this.bhm.addHandler(MainBus, 'videoLoaded', id => {
      this.videoId = id
    })
  },
  methods: {
    generateVideo () {
      this.generatingVideo = true
      Api.postV2('containers/' + this.file.id_file + '/generate-video')
        .then(response => {})
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          // this.generatingVideo = false
        })
    },
    downloadVideo () {
      this.$store.dispatch('dialog/setProps', Object.assign(this.$store.state.dialog.dialog.props || {}, {videoId: this.videoId}))
      Api.download(`document/${this.videoId}/download`)
    },
    shareNativeCompatible () {
      return shareNativeCompatible()
    },
    async shareVideoNative () {
      if (this.videoURL === '') {
        await this.createVideoMagicLink()
      }
      if (this.videoURL !== '') {
        shareNative({
          url: this.videoURL,
          text:
            this.$tf('videoNativeShare', { filename: this.file.name }) + `\n\n`,
          title: `${this.$store.getters['session/accountName']} ${this.$tf(
            'wants to share a video with you'
          )} | Idonia`
        })
      }
    },
    async videoMailTo () {
      if (this.videoURL === '') {
        await this.createVideoMagicLink()
      }
      if (this.videoURL !== '') {
        mailTo({
          url: this.videoURL,
          text:
            this.$tf('videoNativeShare', { filename: this.file.name }) + `\n\n`,
          title: `${this.$store.getters['session/accountName']} ${this.$tf(
            'wants to share a video with you'
          )} | Idonia`
        })
      }
    },
    async shareVideoNativeIOS () {
      if (this.videoURL === '') {
        await this.createVideoMagicLink()
      }
      console.log('videoURL', this.videoURL)
      if (this.videoURL !== '') {
        shareNative({
          text: this.videoURL
        })
      }
    },
    copyToClipboard (text) {
      copyToClipboard(text)
        .then(() => {
          this.$store.dispatch('feedback/addSnack', {
            text: 'Copied to clipboard'
          })
        })
        .catch(error => {
          this.$store.dispatch('feedback/addSnack', {
            text: error.message,
            type: 'error'
          })
        })
    },
    async createVideoMagicLink () {
      if (this.videoExists && this.videoURL === '') {
        try {
          const response = await Api.postV2('public-url', {
            data: {
              namespace: this.$store.state.session.account.username,
              file_id: this.videoId
            }
          })

          if (response.ok) {
            this.videoURL = `${window.location.origin}/v/${response.body
              .namespace +
              '#' +
              response.body.secure_id}`
            this.$store.dispatch('feedback/addSnack', {
              text: this.$tf('Magic link of video enabled'),
              action: {
                label: this.$tf('Share'),
                handler: shareNativeCompatible()
                  ? this.shareVideoNative
                  : () => {
                    this.copyToClipboard(this.videoURL)
                  }
              }
            })
            MainBus.$emit('reloadFiles')
          } else {
            if (response && response.body && response.body.error) {
              this.$store.dispatch('feedback/addSnack', {
                text: this.$tf(
                  'An error occurred while trying to share the video'
                ),
                color: 'error'
              })
            }
          }
        } catch (response) {
          if (response && response.body && response.body.error) {
            this.$store.dispatch('feedback/addSnack', {
              text: this.$tf(
                'An error occurred while trying to share the video'
              ),
              color: 'error'
            })
          }
        } finally {
          this.$store.dispatch('dialog/setLoading', false)
        }
      }
    },
    showVideoSharePanel () {
      this.videoPromise.then(() => {
        this.generatingVideo = false
        this.$store.dispatch('dialog/showDialog', {
          type: 'share',
          props: { file: this.video }
        })
        this.$store.dispatch('dialog/closeDialog')
      })
    }
  }
}
</script>
<style scoped lang="scss">
.share-video-wrapper {
  margin-bottom: 2rem;
  .title {
    font-size: 20px;
    font-weight: bold;
    margin: 1rem 0;
  }
  .button-wrapper {
    display: flex;
    justify-content: center;
    .btn {
      display: inline-block;
    }
  }
  .error-msg {
    color: red;
  }
  .video-btn.btn--disabled {
    opacity: 0.5;
  }
  #download-video-btn {
    background-color: #46a9d4 !important;
  }
  #share-video-btn {
    background-color: #25d366 !important;
    color:white;
    min-width: 230px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/share/ShareVideoCard.vue